import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Icon } from "semantic-ui-react";
import { Link } from "react-router-dom";
import { useLoginCheck } from "../../../../hooks/useLoginCheck";
// 集計カード・タブ・表・ボタンは特別ナンバー画面のスタイルをそのまま使う。
import "../../../../styles/scss/recital2027SpecialWork.scss";
import "../../../../styles/scss/recital2027SeatOrder.scss";
import SeatFlowPanel from "./SeatFlowPanel";

/**
 * 発表会2027（SLASH GALLERY vol.5）指定席の抽選申込の管理ページ。
 *
 * 設計方針（slash_website/docs/recital2027/SEAT_LOTTERY_DESIGN.md）:
 * - 申込ページは slash_website、テーブルは slash-app が所有する。当落・請求書・抽選結果メール・
 *   入金の反映は slash_admin（この画面と services/recital2027_seat_service.py）。
 * - 1次・2次（round）は上の切り替えで分ける。どちらもこの画面・同じ API で、その次の申込だけを扱う。
 * - 1次の当落は申込一覧のチェックで決める（最初は全員当選。見出しのチェックで表示中を一括で切り替え）。
 *   当選は申込どおりの席数だけ。その家族に結果メールを送るまで変えられる。
 * - 2次の当落は部 × 席種ごとに当選席数を選ぶ（一部だけの当選がある。1席も無ければ落選）。
 *   最初は優先の部（出演する部に 1次の支払い済みの席が無い）だけ申込どおり、ほかの部は 0。
 *   席の役割は申込がすべて終わってから決めるので、帯は 1次の支払い済み＋2次の当選の席数を出す。
 * - 上部の SeatFlowPanel で請求書と結果メールを送る。送信を始めた時点で当落が確定する。
 * - 申込は部ごと（1部・2部）。部ごとの帯は当選（未払い・確定前を含む）の席数を出す。
 * - 未払い（請求済み・期限切れを含む）は自動では取り消さない。運営が「当選取消」で取り消す。
 * - 手動登録は、発表会申込・マンスリー（会員として申込）・特別ナンバーでたどれない出演者だけ
 *   （主に外部の人）。両部割引の判定のため、出演する演目も選ぶ。外部には専用URLを発行し、
 *   メールで送るかコピーして個別に渡す。
 */

const apiBase = process.env.REACT_APP_API_URL;

const SEAT_KEYS = ["S", "A", "B"];
const PART_KEYS = ["1", "2"];

const STATUS_LABELS = {
  applied: "当選・請求前",
  canceled: "取り消し",
  won: "当選・請求前",
  lost: "落選",
  invoiced: "未払い",
  paid: "支払い済み",
  revoked: "当選取消",
};

const STATUS_FILTERS = [
  { value: "active", label: "すべて（取り消しを除く）" },
  { value: "won", label: "当選・請求前" },
  { value: "attention", label: "要確認" },
  { value: "unpaid", label: "未払い" },
  { value: "overdue", label: "期限切れ" },
  { value: "paid", label: "支払い済み" },
  { value: "lost", label: "落選" },
  { value: "revoked", label: "当選取消" },
  { value: "canceled", label: "取り消し" },
];

const SOURCE_LABELS = {
  special: "特別ナンバー",
  monthly_fana: "FANA 発表会強化",
  other: "その他",
};

// 2次だけの絞り込み
const ROUND2_FILTERS = [
  { value: "priority", label: "優先あり" },
  { value: "nonpriority", label: "優先なし" },
  { value: "partial", label: "一部当選" },
];

const KIND_LABELS = { external: "外部（非会員）", member: "会員（発表会未申込）" };

const countsTotal = (counts) =>
  PART_KEYS.reduce((n, p) => n + SEAT_KEYS.reduce((m, k) => m + (counts?.[p]?.[k] || 0), 0), 0);

const ZERO_COUNTS = { 1: { S: 0, A: 0, B: 0 }, 2: { S: 0, A: 0, B: 0 } };

// 当選席数を部ごとに作り直す。fn(part) が "full" なら申込どおり、"zero" なら 0、それ以外は今のまま
const rebuildWon = (a, fn) => {
  const cur = a.won || ZERO_COUNTS;
  const out = {};
  for (const p of PART_KEYS) {
    const how = fn(p);
    out[p] = {};
    for (const k of SEAT_KEYS) {
      out[p][k] = how === "full" ? a.requested[p][k] : how === "zero" ? 0 : cur[p]?.[k] || 0;
    }
  }
  return out;
};

const sameCounts = (x, y) => PART_KEYS.every((p) => SEAT_KEYS.every((k) => (x?.[p]?.[k] || 0) === (y?.[p]?.[k] || 0)));

const initialRound = () => {
  try {
    return new URLSearchParams(window.location.search).get("round") === "2" ? 2 : 1;
  } catch {
    return 1;
  }
};

const yen = (n) => `¥${(n || 0).toLocaleString()}`;

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes(),
  ).padStart(2, "0")}`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [, m, d] = iso.split("-").map(Number);
  return `${m}/${d}`;
};

const partText = (counts, p) =>
  SEAT_KEYS.filter((k) => (counts?.[p]?.[k] || 0) > 0)
    .map((k) => `${k}${counts[p][k]}`)
    .join(" ") || "—";

const EMPTY_FORM = {
  id: null,
  kind: "external",
  customer: null,
  first_name: "", // 姓（customers と同じく first_name が姓）
  last_name: "", // 名
  mail: "",
  source: "monthly_fana",
  work_id: "",
  note: "",
};

function SeatOrderPage() {
  useLoginCheck();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [tab, setTab] = useState("applications");
  const [statusFilter, setStatusFilter] = useState("active");
  const [query, setQuery] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [formOpen, setFormOpen] = useState(false);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [round, setRound] = useState(initialRound);

  const fetchOverview = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/api/get_recital2027_seat_overview/?round=${round}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`取得に失敗しました (${res.status})`);
      const json = await res.json();
      // 切り替えの途中で前の次の応答が後から届いたときは捨てる
      if ((json.round || 1) === round) setData(json);
      setError("");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [round]);

  const switchRound = (n) => {
    if (n === round) return;
    setRound(n);
    setData(null);
    setLoading(true);
    setStatusFilter("active");
    try {
      const url = new URL(window.location.href);
      if (n === 2) url.searchParams.set("round", "2");
      else url.searchParams.delete("round");
      window.history.replaceState(null, "", url.toString());
    } catch {
      // URL に残せなくても切り替えはできる
    }
  };

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(""), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const post = useCallback(async (path, body) => {
    const res = await fetch(`${apiBase}/api/${path}/`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || `失敗しました (${res.status})`);
    return json;
  }, []);

  const applications = useMemo(() => data?.applications || [], [data]);
  const performers = useMemo(() => data?.performers || [], [data]);
  const works = useMemo(() => data?.works || [], [data]);
  const candidates = useMemo(() => data?.enhance_candidates || [], [data]);
  const summary = data?.summary;
  const familiesByPart = data?.performer_families; // 集計に失敗したときは null（欄に出さない）
  const familiesTotalByPart = data?.performer_families_total; // 2次のときだけ: 1次と2次の合算

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return applications.filter((a) => {
      if (statusFilter === "active" && a.status === "canceled") return false;
      if (statusFilter === "unpaid" && a.status !== "invoiced") return false;
      if (statusFilter === "won" && !["applied", "won"].includes(a.status)) return false;
      if (["paid", "lost", "canceled", "revoked"].includes(statusFilter) && a.status !== statusFilter) return false;
      if (statusFilter === "overdue" && !(a.status === "invoiced" && a.overdue)) return false;
      if (statusFilter === "attention" && !a.attention) return false;
      if (statusFilter === "priority" && !(a.priority || []).length) return false;
      if (statusFilter === "nonpriority" && (a.priority || []).length) return false;
      if (statusFilter === "partial") {
        const w = countsTotal(a.won);
        if (!(w > 0 && w < countsTotal(a.requested))) return false;
      }
      if (!q) return true;
      return `${a.buyer_name} ${a.buyer_mail}`.toLowerCase().includes(q);
    });
  }, [applications, statusFilter, query]);

  // 会員検索（会員の出演者を登録するとき）
  useEffect(() => {
    if (!formOpen || form.kind !== "member" || form.id) return undefined;
    const q = customerQuery.trim();
    if (q.length < 2 && !/^\d+$/.test(q)) {
      setCustomerResults([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `${apiBase}/api/search_recital2027_seat_customers/?q=${encodeURIComponent(q)}`,
          { credentials: "include" },
        );
        const json = await res.json();
        setCustomerResults(json.customers || []);
      } catch {
        setCustomerResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [customerQuery, form.kind, form.id, formOpen]);

  const enhanceWork = works.find((w) => w.title === "発表会強化");

  const openCreate = (prefill = {}) => {
    setForm({ ...EMPTY_FORM, work_id: enhanceWork ? String(enhanceWork.id) : "", ...prefill });
    setCustomerQuery("");
    setCustomerResults([]);
    setFormOpen(true);
  };

  const openEdit = (p) => {
    setForm({
      id: p.id,
      kind: p.kind,
      customer: p.kind === "member" ? { id: p.customer_id, name: p.name, mail: p.mail } : null,
      first_name: p.first_name,
      last_name: p.last_name,
      mail: p.mail,
      source: p.source,
      work_id: p.work_id ? String(p.work_id) : "",
      note: p.note,
    });
    setFormOpen(true);
  };

  const savePerformer = async () => {
    setBusy(true);
    try {
      const body = {
        kind: form.kind,
        customer_id: form.customer?.id,
        first_name: form.first_name,
        last_name: form.last_name,
        mail: form.mail,
        source: form.source,
        work_id: form.work_id,
        note: form.note,
      };
      if (form.id) await post("update_recital2027_seat_performer", { ...body, id: form.id });
      else await post("create_recital2027_seat_performer", body);
      setFormOpen(false);
      setToast(form.id ? "出演者を更新しました" : "出演者を登録しました");
      await fetchOverview();
    } catch (e) {
      setToast(e.message);
    } finally {
      setBusy(false);
    }
  };

  const deletePerformer = async (p) => {
    if (!window.confirm(`${p.name} さんの登録を削除しますか？\n外部の方は専用URLも使えなくなります。`)) return;
    try {
      await post("delete_recital2027_seat_performer", { id: p.id });
      setToast("削除しました");
      await fetchOverview();
    } catch (e) {
      setToast(e.message);
    }
  };

  const sendInvite = async (p) => {
    const again = p.token_sent_at ? "（送信済みです。もう一度送ります）" : "";
    if (!window.confirm(`${p.name} さん（${p.mail}）に専用URLをメールで送りますか？${again}`)) return;
    try {
      await post("send_recital2027_seat_invite", { id: p.id });
      setToast("専用URLを送信しました");
      await fetchOverview();
    } catch (e) {
      setToast(e.message);
    }
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setToast("専用URLをコピーしました");
    } catch {
      window.prompt("コピーしてください", url);
    }
  };

  // 当落を決める（結果メールを送るまで）。result は "won" / "lost"
  // 未払いの当選を取り消す（請求書を無効にし、取り消しのメールを送る）
  const revoke = async (a) => {
    if (!window.confirm(`${a.buyer_name} さんの当選を取り消しますか？\n請求書を無効にし、取り消しのメールを送ります。`)) return;
    setBusy(true);
    try {
      const res = await post("revoke_recital2027_seat_applications", { ids: [a.id] });
      const r = res.results[0];
      setToast(!r ? "取り消せませんでした（状態が変わっています）" : r.ok ? "当選を取り消しました" : r.error);
      await fetchOverview();
    } catch (e) {
      setToast(e.message);
    } finally {
      setBusy(false);
    }
  };

  // 繰り上げ当選にする・戻す（請求書と繰り上げ当選のメールは、上の「送信する」で送る）
  const promote = async (a, undo) => {
    const msg = undo
      ? `${a.buyer_name} さんの繰り上げ当選を取り消して、落選に戻しますか？`
      : `${a.buyer_name} さんを繰り上げ当選にしますか？\n上の「送信する」で、請求書と繰り上げ当選のメールを送ります。`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await post("promote_recital2027_seat_applications", { ids: [a.id], undo });
      setToast(undo ? "落選に戻しました" : "繰り上げ当選にしました");
      await fetchOverview();
    } catch (e) {
      setToast(e.message);
    } finally {
      setBusy(false);
    }
  };

  const setResults = async (ids, result) => {
    if (ids.length === 0) return;
    setBusy(true);
    try {
      await post("set_recital2027_seat_results", { ids, result });
      await fetchOverview();
    } catch (e) {
      setToast(e.message);
    } finally {
      setBusy(false);
    }
  };

  // 2次: 当選席数を保存する。items: [{id, won}]
  const saveWon = async (items, message) => {
    const changed = items.filter((it) => !sameCounts(it.won, applications.find((a) => a.id === it.id)?.won));
    if (changed.length === 0) return;
    setBusy(true);
    try {
      const res = await post("set_recital2027_seat_won_counts", { items: changed });
      if (message) setToast(message.replace("{n}", res.updated));
      await fetchOverview();
    } catch (e) {
      setToast(e.message);
    } finally {
      setBusy(false);
    }
  };

  const setWonCell = (a, p, k, n) => {
    const won = rebuildWon(a, () => "keep");
    won[p][k] = n;
    saveWon([{ id: a.id, won }]);
  };

  const editableShown = filtered.filter((a) => a.can_edit_won);
  const bulkPriorityFull = () => {
    const items = editableShown
      .filter((a) => (a.priority || []).length)
      .map((a) => ({ id: a.id, won: rebuildWon(a, (p) => ((a.priority || []).includes(Number(p)) ? "full" : "keep")) }));
    if (items.length === 0) return;
    if (!window.confirm(`表示中の ${items.length}家族の「優先の部」を申込どおり当選にしますか？`)) return;
    saveWon(items, "{n}家族を変えました");
  };
  const bulkNonPriorityZero = () => {
    const items = editableShown.map((a) => ({
      id: a.id,
      won: rebuildWon(a, (p) => ((a.priority || []).includes(Number(p)) ? "keep" : "zero")),
    }));
    if (items.length === 0) return;
    if (!window.confirm(`表示中の ${items.length}家族の「優先でない部」を 0席にしますか？`)) return;
    saveWon(items, "{n}家族を変えました");
  };

  // 見出しのチェック: 表示中で当落を変えられる家族を、まとめて当選／落選にする
  const decidableShown = filtered.filter((a) => a.decidable);
  const allShownChecked = decidableShown.length > 0 && decidableShown.every((a) => a.checked);
  const toggleAllShown = () => {
    const result = allShownChecked ? "lost" : "won";
    const ids = decidableShown.filter((a) => a.checked !== (result === "won")).map((a) => a.id);
    if (ids.length === 0) return;
    const label = result === "won" ? "当選" : "落選";
    if (!window.confirm(`表示中の ${ids.length}家族を「${label}」にしますか？`)) return;
    setResults(ids, result);
  };
  const wonCount = applications.filter((a) => a.checked && a.status !== "canceled").length;
  const lostCount = applications.filter((a) => !a.checked && ["applied", "won", "lost"].includes(a.status)).length;
  const partialCount = applications.filter((a) => {
    const w = countsTotal(a.won);
    return a.status !== "canceled" && w > 0 && w < countsTotal(a.requested);
  }).length;
  const roundCounts = data?.round_counts || {};

  const formValid =
    form.kind === "member"
      ? !!form.customer
      : form.first_name.trim() && form.last_name.trim() && form.mail.includes("@");

  // 部ごとの帯: 当選（確定前の当選・未払いを含む）の席数
  const band = summary ? summary.won : null;

  return (
    <div className="recital2027-special-page recital2027-seat-page">
      <div className="page-header">
        <div>
          <p className="eyebrow">SLASH GALLERY vol.5</p>
          <h1>指定席の抽選申込</h1>
          <p className="header-sub">
            申込は slash_website の指定席ページで行います。当落の決定・請求書と結果メールの送信はこの画面で行います。
          </p>
        </div>
        <button type="button" className="ghost-btn" onClick={fetchOverview}>
          <Icon name="refresh" /> 再読み込み
        </button>
      </div>

      <div className="tabs seat-round-tabs" role="tablist" aria-label="何次の申込か">
        {[1, 2].map((n) => (
          <button
            key={n}
            type="button"
            className="tab"
            role="tab"
            aria-selected={round === n}
            onClick={() => switchRound(n)}
          >
            {n}次の申込
            {roundCounts[String(n)] != null && <span className="c">{roundCounts[String(n)]}</span>}
          </button>
        ))}
      </div>

      {error && <div className="alert">{error}</div>}

      {data?.progress && <SeatFlowPanel data={data} post={post} refresh={fetchOverview} setToast={setToast} />}

      {/* 2次: 1次の支払い済み＋2次の当選（席の役割はまだ決めないので、席数の合計だけを出す）。部ごとに 1 枚 */}
      {band && round === 2 && summary.round1_paid && (
        <div className={`seat-band2-wrap${summary.seat_survey ? " seat-band2-wrap--survey" : ""}`}>
          {PART_KEYS.map((key) => {
            const r1 = summary.round1_paid[key];
            const r2 = band[key];
            const sum = (o) => SEAT_KEYS.reduce((n, k) => n + (o[k] || 0), 0);
            const total = { S: r1.S + r2.S, A: r1.A + r2.A, B: r1.B + r2.B };
            const diff = summary.capacity - sum(total);
            return (
              <div className="seat-demand seat-band2" key={key}>
                <div className="sb2-head">
                  <span className="sb2-part">{key}部の席数</span>
                  <span className={`sd-state ${diff < 0 ? "over" : "ok"}`}>
                    {diff < 0 ? `${-diff}席超過` : `あと${diff}席（関係者席・生徒席に使う想定）`}
                  </span>
                </div>
                <table className="sb2-table">
                  <thead>
                    <tr>
                      <th />
                      <th className="s">S</th>
                      <th className="a">A</th>
                      <th className="b">B</th>
                      <th>計</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th>1次 支払い済み</th>
                      {SEAT_KEYS.map((k) => (
                        <td key={k}>{r1[k]}</td>
                      ))}
                      <td>{sum(r1)}</td>
                    </tr>
                    <tr>
                      <th>
                        2次 当選<small>{r2.families}家族</small>
                      </th>
                      {SEAT_KEYS.map((k) => (
                        <td key={k}>{r2[k]}</td>
                      ))}
                      <td>{r2.total}</td>
                    </tr>
                    <tr className="sb2-sum">
                      <th>合計</th>
                      {SEAT_KEYS.map((k) => (
                        <td key={k} className={k.toLowerCase()}>
                          {total[k]}
                        </td>
                      ))}
                      <td>{sum(total)}</td>
                    </tr>
                  </tbody>
                </table>
                <span className="sd-note">
                  2次の当選は確定前の当選席数を含みます（落選・取り消しを除く）。使用できる席 {summary.capacity}席（目安）
                </span>
              </div>
            );
          })}
          {/* 会員アプリの座席アンケート（出演者さまの席も申し込んだか）。家族ごとの回答で 1次・2次の区別はない */}
          {summary.seat_survey && (() => {
            const s = summary.seat_survey;
            const answered = s.yes + s.no;
            const pct = (n, d) => (d ? `${Math.round((n / d) * 100)}%` : "-");
            return (
              <div className="seat-demand seat-band2 seat-survey">
                <div className="sb2-head">
                  <span className="sb2-part">座席アンケート</span>
                </div>
                <table className="sb2-table">
                  <thead>
                    <tr>
                      <th />
                      <th>家族</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th>はい</th>
                      <td>{s.yes}</td>
                    </tr>
                    <tr>
                      <th>いいえ</th>
                      <td>{s.no}</td>
                    </tr>
                    <tr className="sb2-sum">
                      <th>回答</th>
                      <td>{answered}</td>
                    </tr>
                  </tbody>
                </table>
                <span className="sd-note">
                  対象 {s.targets}家族・回答率 {pct(answered, s.targets)}・はいの割合 {pct(s.yes, answered)}
                </span>
              </div>
            );
          })()}
        </div>
      )}

      {/* 部ごとの席数（両部に申し込んだ家族は両方の部に数える） */}
      {band && round === 1 && (
        <div className="seat-demand">
          <div className="sd-head">
            <span className="sd-title">当選の座席（部ごと）</span>
            <span className="sd-note">
              使用できる席 {summary.capacity}席（1階 {summary.capacity_floor1_total}席から1列目の
              {summary.camera_seats}席〈カメラ席〉を除いた{summary.capacity_floor1}席＋2階{" "}
              {summary.capacity_floor2}席） / 当選（未払いを含む・落選と取り消しを除く）
            </span>
            {/* 当落のチェックのまま、座席図で収まるかを試せる（確定前はシミュレーション） */}
            <Link className="ghost-btn sd-link" to="/management/recital/2027/seat-assign">
              <Icon name="th" /> 座席図で確認
            </Link>
          </div>
          {PART_KEYS.map((key) => {
            const p = band[key];
            const diff = summary.capacity - p.total;
            return (
              <div className="sd-row" key={key}>
                <span className="sd-part">{key}部</span>
                <span className="sd-seat s">S <b>{p.S}</b></span>
                <span className="sd-seat a">A <b>{p.A}</b></span>
                <span className="sd-seat b">B <b>{p.B}</b></span>
                <span className="sd-total">
                  計 <b>{p.total}</b>席（{p.families}家族）
                </span>
                <span className={`sd-state ${diff < 0 ? "over" : "ok"}`}>
                  {diff < 0 ? `${-diff}席超過` : `あと${diff}席（関係者席・生徒席に使う想定）`}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="loading">読み込み中…</div>
      ) : summary ? (
        <>
          <div className="kpis">
            <div className="kpi">
              <span className="k">申込した家族</span>
              {/* 部ごとの「出演家族のうち申し込んだ家族 / 出演家族」。申込はどの部の席でもよい。
                  両部に出る家族は両方に数える。当選取消だけの家族は申込に数えない。集計に失敗したときは合計だけ出す */}
              {familiesByPart ? (
                <>
                  {/* 2次: 1次・2次のどちらかに申し込んだ家族の割合を大きく出し、2次だけの数はその下に小さく */}
                  {familiesTotalByPart &&
                    PART_KEYS.map((key) => {
                      const f = familiesTotalByPart[key];
                      return (
                        <span className="v small" key={`total-${key}`}>
                          1次＋2次 {key}部 {f.applied} / {f.performing}
                          {f.performing ? `（${Math.round((f.applied / f.performing) * 100)}%）` : ""}
                        </span>
                      );
                    })}
                  {PART_KEYS.map((key) => {
                    const f = familiesByPart[key];
                    return (
                      <span className={familiesTotalByPart ? "sub" : "v small"} key={key}>
                        {familiesTotalByPart ? "2次 " : ""}
                        {key}部 {f.applied} / {f.performing}
                        {f.performing ? `（${Math.round((f.applied / f.performing) * 100)}%）` : ""}
                      </span>
                    );
                  })}
                  <span className="sub">
                    申込家族（当選取消は除く） / 出演家族
                    {familiesByPart.pending.performing ? `（部の調整中 ${familiesByPart.pending.performing}家族は除く）` : ""}
                  </span>
                </>
              ) : (
                <span className="v">
                  {summary.families}
                  <small>家族</small>
                </span>
              )}
            </div>
            <div className="kpi">
              <span className="k">当落</span>
              <span className="v small">
                当選 {wonCount}・落選 {lostCount}
              </span>
              {round === 2 && (
                <span className="sub">
                  うち一部当選 {partialCount}・優先あり {summary.priority_families || 0}家族
                </span>
              )}
              <span className="sub">{summary.lottery_done ? "確定済み（送信を始めました）" : "未確定（送信を始めると確定）"}</span>
            </div>
            <div className={`kpi${summary.unpaid ? " warn" : ""}`}>
              <span className="k">未払い</span>
              <span className="v">
                {summary.unpaid}
                <small>件</small>
              </span>
              <span className="sub">
                {yen(summary.unpaid_amount)}
                {summary.overdue ? `・うち期限切れ ${summary.overdue}件` : ""}
              </span>
            </div>
            <div className="kpi">
              <span className="k">支払い済み</span>
              <span className="v">
                {summary.by_status.paid || 0}
                <small>件</small>
              </span>
              <span className="sub">{yen(summary.paid_amount)}（税込）</span>
            </div>
          </div>

          <div className="tabrow">
            <div className="tabs" role="tablist">
              <button
                type="button"
                className="tab"
                role="tab"
                aria-selected={tab === "applications"}
                onClick={() => setTab("applications")}
              >
                申込一覧<span className="c">{summary.families}</span>
              </button>
              <button
                type="button"
                className="tab"
                role="tab"
                aria-selected={tab === "performers"}
                onClick={() => setTab("performers")}
              >
                出演者の手動登録<span className="c">{performers.length}</span>
              </button>
            </div>
          </div>

          {tab === "applications" && (
            <section>
              <div className="toolbar">
                <div className="search-bar">
                  <Icon name="search" />
                  <input
                    type="text"
                    placeholder="申込者名・メールで検索"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
                <div className="chipset">
                  {(round === 2 ? [...STATUS_FILTERS.slice(0, 1), ...ROUND2_FILTERS, ...STATUS_FILTERS.slice(1)] : STATUS_FILTERS).map((f) => (
                    <button
                      key={f.value}
                      type="button"
                      className="chip"
                      aria-pressed={statusFilter === f.value}
                      onClick={() => setStatusFilter(f.value)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                <span className="spacer" />
                {round === 2 && (
                  <>
                    <button type="button" className="ghost-btn" disabled={busy} onClick={bulkPriorityFull}>
                      優先の部を申込どおり
                    </button>
                    <button type="button" className="ghost-btn" disabled={busy} onClick={bulkNonPriorityZero}>
                      優先でない部を 0 席
                    </button>
                  </>
                )}
                <span className="dim">{filtered.length} 件</span>
              </div>

              {round === 2 ? (
                <Round2Table
                  rows={filtered}
                  busy={busy}
                  onCell={setWonCell}
                  onRow={(a, how) =>
                    saveWon([{ id: a.id, won: rebuildWon(a, () => how) }])
                  }
                  onRevoke={revoke}
                  onPromote={promote}
                />
              ) : (
              <div className="table-wrap">
                <div className="scroller">
                  <div className="grid seat-orders-grid">
                    <div className="row head">
                      <span className="seat-check-cell">
                        <label className="seat-check" title="表示中の家族をまとめて当選／落選にする">
                          <input
                            type="checkbox"
                            checked={allShownChecked}
                            disabled={busy || decidableShown.length === 0}
                            onChange={toggleAllShown}
                          />
                          <span>当選</span>
                        </label>
                      </span>
                      <span>更新日時</span>
                      <span>申込者</span>
                      <span>1部（申込）</span>
                      <span>2部（申込）</span>
                      <span>状態</span>
                      <span className="right">請求額</span>
                      <span>期限・支払</span>
                      <span>対応</span>
                    </div>
                    {filtered.length === 0 ? (
                      <div className="empty">該当する申込はありません</div>
                    ) : (
                      filtered.map((a) => {
                        return (
                          <div className={`row body static${a.status === "lost" ? " seat-row-lost" : ""}`} key={a.id}>
                            <span className="seat-check-cell">
                              {a.status === "canceled" ? (
                                <span className="dash">—</span>
                              ) : (
                                <input
                                  type="checkbox"
                                  className="seat-check-input"
                                  checked={a.checked}
                                  disabled={busy || !a.decidable}
                                  title={a.decidable ? "チェックで当選、外すと落選" : "結果メールを送った後は変えられません"}
                                  onChange={(e) => setResults([a.id], e.target.checked ? "won" : "lost")}
                                />
                              )}
                            </span>
                            <span className="mono">{fmtDateTime(a.updated_at)}</span>
                            <span>
                              <span className="name">{a.buyer_name || "—"}</span>
                              {a.external && <span className="seat-badge">外部</span>}
                              <span className="cell-sub">{a.buyer_mail}</span>
                            </span>
                            <span className="mono">{partText(a.requested, "1")}</span>
                            <span className="mono">{partText(a.requested, "2")}</span>
                            <span>
                              <span className={`seat-status ${a.status}`}>{STATUS_LABELS[a.status] || a.status}</span>
                              {a.attention && <span className="seat-status lost attention">要確認</span>}
                              {a.promoted && <span className="seat-status won attention">繰り上げ</span>}
                              {a.both_parts && <span className="cell-sub">両部割引 {yen(a.discount)}</span>}
                              {a.status === "lost" && (
                                <span className="cell-sub">{a.result_mailed_at ? "落選メール送信済み" : "未送信"}</span>
                              )}
                              {a.revoked_at && <span className="cell-sub">取消 {fmtDateTime(a.revoked_at)}</span>}
                              {a.adjusted_at && !a.revoked_at && (
                                <span className="cell-sub">変更 {fmtDateTime(a.adjusted_at)}</span>
                              )}
                            </span>
                            <span className="right mono">{a.total ? yen(a.total) : <span className="dash">—</span>}</span>
                            <span className="mono">
                              {a.status === "paid" ? (
                                <>支払 {fmtDateTime(a.paid_at)}</>
                              ) : a.due_date ? (
                                <span className={a.overdue ? "warn-text" : ""}>
                                  期限 {fmtDate(a.due_date)}
                                  {a.overdue && "（期限切れ）"}
                                </span>
                              ) : (
                                <span className="dash">—</span>
                              )}
                            </span>
                            <span className="seat-actions seat-actions--left">
                              {a.can_revoke && (
                                <button type="button" className="danger-btn" disabled={busy} onClick={() => revoke(a)}>
                                  当選取消
                                </button>
                              )}
                              {a.can_promote && (
                                <button type="button" className="ghost-btn" disabled={busy} onClick={() => promote(a, false)}>
                                  繰り上げ当選
                                </button>
                              )}
                              {a.can_unpromote && (
                                <button type="button" className="ghost-btn" disabled={busy} onClick={() => promote(a, true)}>
                                  繰り上げを戻す
                                </button>
                              )}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
              )}
            </section>
          )}

          {tab === "performers" && (
            <section>
              <div className="toolbar">
                <span className="dim">
                  発表会申込・マンスリー（会員として申込）・特別ナンバーでたどれない出演者だけを登録します（主に外部の方）。
                </span>
                <span className="spacer" />
                <button type="button" className="primary-btn" onClick={() => openCreate()}>
                  <Icon name="plus" /> 出演者を登録
                </button>
              </div>

              {candidates.length > 0 && (
                <div className="seat-candidates">
                  <span className="seat-candidates__title">
                    発表会強化（マンスリー）に会員以外で申し込んでいる方（未登録）
                  </span>
                  {candidates.map((c) => (
                    <div className="seat-candidates__row" key={c.mail}>
                      <span>
                        <span className="name">{c.name}</span>
                        <span className="cell-sub">{c.mail}</span>
                      </span>
                      <button
                        type="button"
                        className="ghost-btn"
                        onClick={() =>
                          openCreate({
                            first_name: c.first_name,
                            last_name: c.last_name,
                            mail: c.mail,
                            source: "monthly_fana",
                          })
                        }
                      >
                        <Icon name="plus" /> この方を登録
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="table-wrap">
                <div className="scroller">
                  <div className="grid seat-performers-grid">
                    <div className="row head">
                      <span>区分</span>
                      <span>氏名・メール</span>
                      <span>出演する演目</span>
                      <span>専用URL</span>
                      <span>送信日時</span>
                      <span />
                    </div>
                    {performers.length === 0 ? (
                      <div className="empty">登録されている出演者はいません</div>
                    ) : (
                      performers.map((p) => (
                        <div className="row body static" key={p.id}>
                          <span>
                            <span className={`seat-badge ${p.kind}`}>{KIND_LABELS[p.kind]}</span>
                          </span>
                          <span>
                            <span className="name">{p.name}</span>
                            <span className="cell-sub">
                              {p.mail}
                              {p.customer_id ? `（会員ID ${p.customer_id}）` : ""}
                            </span>
                            {p.note && <span className="cell-sub">{p.note}</span>}
                          </span>
                          <span>
                            {p.work_title || <span className="warn-text">未選択（部を判定できません）</span>}
                            {p.work_part && <span className="cell-sub">{p.work_part}部</span>}
                            <span className="cell-sub">{SOURCE_LABELS[p.source] || p.source}</span>
                          </span>
                          <span>
                            {p.url ? (
                              <button type="button" className="ghost-btn" onClick={() => copyUrl(p.url)}>
                                <Icon name="copy" /> コピー
                              </button>
                            ) : (
                              <span className="dim">ログインで申込</span>
                            )}
                          </span>
                          <span className="mono">{p.kind === "external" ? fmtDateTime(p.token_sent_at) : ""}</span>
                          <span className="right seat-actions">
                            {p.kind === "external" && (
                              <button type="button" className="ghost-btn" onClick={() => sendInvite(p)}>
                                <Icon name="mail" /> URLを送る
                              </button>
                            )}
                            <button type="button" className="ghost-btn" onClick={() => openEdit(p)}>
                              編集
                            </button>
                            <button type="button" className="danger-btn" onClick={() => deletePerformer(p)}>
                              削除
                            </button>
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </section>
          )}
        </>
      ) : null}

      {/* 出演者の登録・編集 */}
      {formOpen && (
        <div className="seat-modal-overlay" onClick={() => !busy && setFormOpen(false)}>
          <div className="seat-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{form.id ? "出演者を編集" : "出演者を登録"}</h2>

            {!form.id && (
              <div className="seat-field">
                <label>区分</label>
                <div className="chipset">
                  {Object.entries(KIND_LABELS).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className="chip"
                      aria-pressed={form.kind === value}
                      onClick={() => setForm((f) => ({ ...f, kind: value, customer: null }))}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {form.kind === "member" ? (
              <div className="seat-field">
                <label>会員</label>
                {form.customer ? (
                  <div className="seat-picked">
                    <span>
                      {form.customer.name}（{form.customer.mail}・会員ID {form.customer.id}）
                    </span>
                    {!form.id && (
                      <button type="button" className="ghost-btn" onClick={() => setForm((f) => ({ ...f, customer: null }))}>
                        変更
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <input
                      className="seat-input"
                      placeholder="氏名・メール・会員IDで検索"
                      value={customerQuery}
                      onChange={(e) => setCustomerQuery(e.target.value)}
                    />
                    <div className="seat-results">
                      {customerResults.map((c) => (
                        <button key={c.id} type="button" onClick={() => setForm((f) => ({ ...f, customer: c }))}>
                          {c.name}
                          <span className="dim">
                            {" "}
                            {c.mail}・{c.studio}・ID {c.id}
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
                <p className="seat-help">会員は通常どおりログインして申し込みます（専用URLは発行しません）。</p>
              </div>
            ) : (
              <>
                <div className="seat-field seat-field--2">
                  <div>
                    <label>姓</label>
                    <input
                      className="seat-input"
                      value={form.first_name}
                      onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label>名</label>
                    <input
                      className="seat-input"
                      value={form.last_name}
                      onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="seat-field">
                  <label>メールアドレス（専用URLの送り先）</label>
                  <input
                    className="seat-input"
                    type="email"
                    value={form.mail}
                    onChange={(e) => setForm((f) => ({ ...f, mail: e.target.value }))}
                  />
                </div>
              </>
            )}

            <div className="seat-field">
              <label>出演する演目（両部割引の判定に使います）</label>
              <select
                className="sel seat-select"
                value={form.work_id}
                onChange={(e) => setForm((f) => ({ ...f, work_id: e.target.value }))}
              >
                <option value="">未選択</option>
                {works.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.title}
                    {w.planned_part ? `（${w.planned_part}部）` : "（部 未定）"}
                  </option>
                ))}
              </select>
            </div>

            <div className="seat-field">
              <label>出どころ</label>
              <select
                className="sel"
                value={form.source}
                onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}
              >
                {Object.entries(SOURCE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="seat-field">
              <label>メモ</label>
              <input
                className="seat-input"
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              />
            </div>

            <div className="seat-modal-actions">
              <button type="button" className="ghost-btn" disabled={busy} onClick={() => setFormOpen(false)}>
                キャンセル
              </button>
              <button type="button" className="primary-btn" disabled={busy || !formValid} onClick={savePerformer}>
                {form.id ? "更新する" : "登録する"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

/* ---------------------------------------------------------------- 2次の申込一覧 */
// 席種ごとに 0〜申込席数を選ぶ。選んだ時点で保存する。
function WonCells({ a, part, busy, onCell }) {
  const req = a.requested[part];
  const keys = SEAT_KEYS.filter((k) => req[k] > 0);
  if (keys.length === 0) return <span className="dash">—</span>;
  const prio = (a.priority || []).includes(Number(part));
  return (
    <span className={`seat-won-cells${prio ? " prio" : ""}`}>
      {keys.map((k) => (
        <label key={k} className="seat-won-cell">
          <span className={`k ${k.toLowerCase()}`}>{k}</span>
          <select
            className="sel"
            value={a.won?.[part]?.[k] || 0}
            disabled={busy || !a.can_edit_won}
            onChange={(e) => onCell(a, part, k, Number(e.target.value))}
          >
            {Array.from({ length: req[k] + 1 }, (_, n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span className="of">/{req[k]}</span>
        </label>
      ))}
    </span>
  );
}

function Round2Table({ rows, busy, onCell, onRow, onRevoke, onPromote }) {
  return (
    <div className="table-wrap">
      <div className="scroller">
        <div className="grid seat-orders-grid seat-orders-grid--r2">
          <div className="row head">
            <span>更新日時</span>
            <span>申込者</span>
            <span>優先・1次</span>
            <span>1部（当選 / 申込）</span>
            <span>2部（当選 / 申込）</span>
            <span>状態</span>
            <span className="right">請求額</span>
            <span>期限・支払</span>
            <span>対応</span>
          </div>
          {rows.length === 0 ? (
            <div className="empty">該当する申込はありません</div>
          ) : (
            rows.map((a) => {
              const w = countsTotal(a.won);
              const req = countsTotal(a.requested);
              const lost = a.status === "lost" || (a.status === "applied" && w === 0);
              return (
                <div className={`row body static${lost ? " seat-row-lost" : ""}`} key={a.id}>
                  <span className="mono">{fmtDateTime(a.updated_at)}</span>
                  <span>
                    <span className="name">{a.buyer_name || "—"}</span>
                    {a.external && <span className="seat-badge">外部</span>}
                    <span className="cell-sub">{a.buyer_mail}</span>
                  </span>
                  <span>
                    {(a.priority || []).length ? (
                      <span className="seat-status won">優先 {a.priority.map((p) => `${p}部`).join("・")}</span>
                    ) : (
                      <span className="dash">優先なし</span>
                    )}
                    <span className="cell-sub">
                      1次 {a.round1_paid ? `1部 ${partText(a.round1_paid, "1")} / 2部 ${partText(a.round1_paid, "2")}` : "支払い済みなし"}
                    </span>
                    {a.round1_discount > 0 && <span className="cell-sub">1次で両部割引済み</span>}
                  </span>
                  <span>
                    <WonCells a={a} part="1" busy={busy} onCell={onCell} />
                  </span>
                  <span>
                    <WonCells a={a} part="2" busy={busy} onCell={onCell} />
                  </span>
                  <span>
                    {a.status === "applied" ? (
                      <span className={`seat-status ${w === 0 ? "lost" : "applied"}`}>
                        {w === 0 ? "落選（確定前）" : w < req ? "一部当選（確定前）" : "当選（確定前）"}
                      </span>
                    ) : (
                      <span className={`seat-status ${a.status}`}>
                        {STATUS_LABELS[a.status] || a.status}
                        {["won", "invoiced", "paid"].includes(a.status) && w < req ? "（一部）" : ""}
                      </span>
                    )}
                    {a.attention && <span className="seat-status lost attention">要確認</span>}
                    {a.promoted && <span className="seat-status won attention">繰り上げ</span>}
                    {a.discount > 0 && <span className="cell-sub">両部割引 {yen(a.discount)}</span>}
                    {a.status === "lost" && (
                      <span className="cell-sub">{a.result_mailed_at ? "落選メール送信済み" : "未送信"}</span>
                    )}
                    {a.revoked_at && <span className="cell-sub">取消 {fmtDateTime(a.revoked_at)}</span>}
                    {a.status === "applied" && (
                      <span className="cell-sub">{a.adjusted ? `変更 ${fmtDateTime(a.adjusted_at)}` : "既定（優先の部だけ当選）"}</span>
                    )}
                  </span>
                  <span className="right mono">{a.total ? yen(a.total) : <span className="dash">—</span>}</span>
                  <span className="mono">
                    {a.status === "paid" ? (
                      <>支払 {fmtDateTime(a.paid_at)}</>
                    ) : a.due_date ? (
                      <span className={a.overdue ? "warn-text" : ""}>
                        期限 {fmtDate(a.due_date)}
                        {a.overdue && "（期限切れ）"}
                      </span>
                    ) : (
                      <span className="dash">—</span>
                    )}
                  </span>
                  <span className="seat-actions seat-actions--left">
                    {a.can_edit_won && (
                      <>
                        <button type="button" className="ghost-btn" disabled={busy || w === req} onClick={() => onRow(a, "full")}>
                          申込どおり
                        </button>
                        {!a.promoted && (
                          <button type="button" className="ghost-btn" disabled={busy || w === 0} onClick={() => onRow(a, "zero")}>
                            0 席
                          </button>
                        )}
                      </>
                    )}
                    {a.can_revoke && (
                      <button type="button" className="danger-btn" disabled={busy} onClick={() => onRevoke(a)}>
                        当選取消
                      </button>
                    )}
                    {a.can_promote && (
                      <button type="button" className="ghost-btn" disabled={busy} onClick={() => onPromote(a, false)}>
                        繰り上げ当選
                      </button>
                    )}
                    {a.can_unpromote && (
                      <button type="button" className="ghost-btn" disabled={busy} onClick={() => onPromote(a, true)}>
                        繰り上げを戻す
                      </button>
                    )}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default SeatOrderPage;
